import { useState, useCallback } from 'react';
import { Tag, Tooltip, Avatar } from 'antd';
import PayoutDaysDisplay from 'src/_shared/components/index';
import note from 'src/_shared/assets/note.png';
import { mapBonusStatus, translation } from 'src/_shared/services/utils';
import getSymbolFromCurrency from 'currency-symbol-map';
import { ml } from 'src/_shared/services/utils';

const getBonusTagColor = (bonusStatus) => {
	const bonusStatusColors = {
		pending: 'var(--tan-hide)',
		needsReview: 'var(--mellow-yellow)',
		earned: 'var(--fern)',
		ineligible: 'var(--sunset-orange)',
		paid: 'var(--forest-green)',
	};
	return bonusStatusColors[bonusStatus] || 'default';
};

const BonusInfo = ({
	allMultiLingualData,
	referral,
	myReferralBonus,
	totalBonus,
	currentTieredBonus,
	currentUser,
}) => {
	const [tooltipTitle, setTooltipTitle] = useState(referral?.adminNote || '');
	const languageCode = currentUser?.languageCode;
	const currencyCode = currentUser?.userGroup?.currency || 'USD';

	const handleTooltipVisibleChange = useCallback(
		async (visible) => {
			if (visible) {
				if (
					languageCode &&
					languageCode !== 'EN' &&
					languageCode !== 'US' &&
					referral?.adminNote
				) {
					try {
						const adminNoteTranslated = await translation(referral.adminNote, {
							languageCode,
						});
						setTooltipTitle(adminNoteTranslated);
					} catch (error) {
						console.error(error);
						setTooltipTitle(referral.adminNote);
					}
				} else {
					setTooltipTitle(referral.adminNote);
				}
			}
		},
		[referral?.adminNote, languageCode]
	);

	const potentialBonus =
		currentTieredBonus?.tiers?.reduce((total, tier) => {
			try {
				if (typeof tier !== 'string') {
					console.warn('Tier is not a valid string:', tier);
					return total;
				}

				const parsedTier = JSON.parse(tier);

				if (parsedTier?.userGroup === currentUser?.userGroupId) {
					return total + (parsedTier.amount || 0);
				} else {
					return total;
				}
			} catch (e) {
				console.error('Invalid JSON string:', tier, e);
				return total;
			}
		}, 0) || 0;
	const preStartUserGroupName = currentUser?.company?.userGroups.find(
		(group) => group.id === currentUser?.company.preStartUserGroupId
	)?.name;
	const preStartEnabled =
		currentUser?.company?.preStartEnabled &&
		preStartUserGroupName &&
		referral?.origin === 'pre_start';

	return (
		<div className="bonus-info">
			{preStartEnabled && <Tag color="green">{preStartUserGroupName}</Tag>}
			{referral.adminNote && referral.status !== 'ineligible' && (
				<Tooltip
					arrowPointAtCenter
					placement="topLeft"
					title={tooltipTitle}
					onOpenChange={handleTooltipVisibleChange}
					key={`${referral.id}-admin-note-2`}
				>
					<img className="admin-note-icon" alt="admin-note" src={note} />
				</Tooltip>
			)}

			{referral.status !== 'ineligible' &&
				referral.status !== 'notHired' &&
				referral.status !== 'declined' && (
					<>
						{myReferralBonus.length > 0 ? (
							<>
								<Avatar.Group>
									{myReferralBonus
										.sort(
											(a, b) => new Date(a.earnedDate) - new Date(b.earnedDate)
										)
										.map((bonus, index) => {
											const bonusStatus =
												currentUser.role !== 'admin' &&
												bonus.bonusStatus === 'needsReview'
													? 'pending'
													: bonus.bonusStatus;
											const bonusLabel = mapBonusStatus(
												bonusStatus,
												currentUser?.company,
												`${bonusStatus.charAt(0).toUpperCase()}${bonusStatus.slice(1)}`
											);
											return (
												<Tooltip
													key={index}
													title={`${bonusLabel}: ${ml('Payment', currentUser, allMultiLingualData)} ${bonus.payment}`}
												>
													<Avatar
														style={{
															backgroundColor: getBonusTagColor(bonusStatus),
														}}
													>
														{bonusLabel.charAt(0).toUpperCase()}
													</Avatar>
												</Tooltip>
											);
										})}
								</Avatar.Group>

								{totalBonus > 0 && (
									<span className="total-bonus">
										{getSymbolFromCurrency(currencyCode)}
										{totalBonus.toLocaleString()}
									</span>
								)}
								{referral?.status !== 'hired' && (
									<span className="total-bonus">
										{getSymbolFromCurrency(currencyCode)}
										{referral?.bonus?.amount}
									</span>
								)}
							</>
						) : (
							<PayoutDaysDisplay
								currentUser={currentUser}
								allMultiLingualData={allMultiLingualData}
								tiers={
									currentTieredBonus?.tiers ??
									currentTieredBonus?.tieredBonus?.tiers ??
									[]
								}
							/>
						)}
					</>
				)}
			{referral?.status === 'hired' ||
			referral?.status === 'notHired' ||
			referral?.status === 'declined' ||
			potentialBonus <= 0 ||
			potentialBonus === undefined ? null : (
				<span className="total-bonus">
					{getSymbolFromCurrency(currencyCode)}
					{potentialBonus}
				</span>
			)}
		</div>
	);
};

export default BonusInfo;
